import type { AgentToolDescriptor } from "./agentTypes";
import type { ToolResult } from "./tools";

type Schema = Record<string, unknown>;
type Container = "object" | "array";

export const WRONG_SHAPE = "WRONG_SHAPE";
export const CHANGES_NOTHING = "CHANGES_NOTHING";

const FIELD_SOURCES = "fieldSources";

const variantsOf = (schema: Schema): Schema[] => [schema, ...((schema.allOf as Schema[] | undefined) ?? [])];

const containerOf = (schema: Schema): Container | null => {
  for (const variant of variantsOf(schema)) {
    if (variant.type === "array" || variant.items) return "array";
    if (variant.type === "object" || variant.properties) return "object";
  }
  return null;
};

const propertiesOf = (schema: Schema): Record<string, Schema> =>
  Object.assign({}, ...variantsOf(schema).map((variant) => (variant.properties as Record<string, Schema>) ?? {}));

const itemsOf = (schema: Schema): Schema | undefined =>
  variantsOf(schema)
    .map((variant) => variant.items as Schema | undefined)
    .find(Boolean);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const fits = (value: unknown, container: Container) => (container === "array" ? Array.isArray(value) : isPlainObject(value));

const parsedJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const decoded = (value: unknown, schema: Schema): unknown => {
  const container = containerOf(schema);
  if (!container) return value;
  const candidate = typeof value === "string" ? parsedJson(value) : value;
  if (!fits(candidate, container)) return value;
  if (Array.isArray(candidate)) {
    const items = itemsOf(schema);
    return items ? candidate.map((item) => decoded(item, items)) : candidate;
  }
  const properties = propertiesOf(schema);
  return Object.fromEntries(
    Object.entries(candidate as Record<string, unknown>).map(([key, child]) => [
      key,
      properties[key] ? decoded(child, properties[key]) : child,
    ]),
  );
};

export const withJsonTextDecoded = (descriptor: AgentToolDescriptor, args: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(args).map(([name, value]) => {
      const param = descriptor.params.find((candidate) => candidate.name === name);
      return [name, param ? decoded(value, param.schema) : value];
    }),
  );

const misshapenPaths = (value: unknown, schema: Schema, path: string): string[] => {
  if (value === null || value === undefined) return [];
  const container = containerOf(schema);
  if (!container) return [];
  if (!fits(value, container)) return [`${path} (${container === "array" ? "en liste" : "et objekt"})`];
  if (Array.isArray(value)) {
    const items = itemsOf(schema);
    return items ? value.flatMap((item, index) => misshapenPaths(item, items, `${path}[${index}]`)) : [];
  }
  const properties = propertiesOf(schema);
  return Object.entries(value as Record<string, unknown>).flatMap(([key, child]) =>
    properties[key] ? misshapenPaths(child, properties[key], `${path}.${key}`) : [],
  );
};

export const wrongShapeRefusal = (descriptor: AgentToolDescriptor, args: Record<string, unknown>): ToolResult | null => {
  const misshapen = descriptor.params.flatMap((param) => misshapenPaths(args[param.name], param.schema, param.name));
  if (misshapen.length === 0) return null;
  return {
    ok: false,
    deviationCode: WRONG_SHAPE,
    message:
      `Ikke sendt: ${misshapen.join(", ")} har feil form. Send hvert felt med formen i verktøyets skjema, ` +
      "objekter og lister som JSON, ikke som tekst. Brukeren har ikke sett noe forslag ennå.",
  };
};

export const changesNothingRefusal = (descriptor: AgentToolDescriptor, args: Record<string, unknown>): ToolResult | null => {
  const body = descriptor.params.find((param) => param.in === "body");
  if (!body || !propertiesOf(body.schema)[FIELD_SOURCES]) return null;
  const value = args[body.name];
  if (!isPlainObject(value)) return null;
  const changes = Object.entries(value).filter(([key, child]) => key !== FIELD_SOURCES && child !== undefined);
  if (changes.length > 0) return null;
  return {
    ok: false,
    deviationCode: CHANGES_NOTHING,
    message: `Ikke sendt: ${body.name} inneholder ingen felt å endre. Legg feltene du endrer inn i ${body.name}, ved siden av fieldSources.`,
  };
};
